import React, { useEffect, useMemo, useState } from 'react';
import { Accordion, Alert, Badge, Button, Modal, Spinner, Tab, Tabs } from 'react-bootstrap';
import {
  FaCalendarAlt,
  FaClock,
  FaHistory,
  FaStickyNote,
  FaUser,
  FaUserEdit,
} from 'react-icons/fa';

const getStatusBadge = (estado) => {
  if (!estado) return <Badge bg="secondary">Sem Estado</Badge>;
  const est = String(estado).toLowerCase().trim();
  if (est.includes('atendido')) return <Badge bg="success">Atendido</Badge>;
  if (est.includes('chamado'))
    return (
      <Badge bg="warning" text="dark">
        Chamado
      </Badge>
    );
  if (est.includes('em atendimento')) return <Badge bg="primary">Em Atendimento</Badge>;
  if (est.includes('marcada'))
    return (
      <Badge bg="info" text="dark">
        Marcada
      </Badge>
    );
  if (est.includes('cancelad')) return <Badge bg="danger">Cancelado</Badge>;
  return <Badge bg="secondary">{estado}</Badge>;
};

// Timeline de Logs reutilizável
const HistoryTimeline = ({ items }) => {
  return (
    <div className="timeline-container position-relative ps-3">
      {items.map((item, index) => {
        const itemKey = `item-${index}`;
        return (
          <div
            key={itemKey}
            className="timeline-item position-relative pb-3 ps-4 border-start border-2 border-primary-subtle"
          >
            <span
              className="position-absolute top-0 start-0 translate-middle rounded-circle bg-white p-1 border border-2 border-primary d-flex align-items-center justify-content-center"
              style={{ width: '24px', height: '24px', marginLeft: '-1px' }}
            >
              <FaUserEdit className="text-primary" size={10} />
            </span>

            <div className="bg-light p-2 rounded border">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-1">
                <span className="fw-bold text-dark small">
                  * {item.EVOLUCAO || 'Alteração de registro'} --- {item.ESTADO} |{' '}
                  {item.STATUS_CONFIRMA}
                </span>
                <span className="text-muted ms-auto" style={{ fontSize: '0.75rem' }}>
                  <FaClock size={10} className="me-1" />
                  {item.HORAATUALIZADA_FORMATADA}
                </span>
              </div>

              {/* Exibe o nome do paciente caso tenha ocorrido troca no mesmo agendamento */}
              {item.NOMEPAC && (
                <div className="text-primary small mt-1 d-flex align-items-center gap-1">
                  <FaUser size={10} />
                  <span>
                    <strong>Paciente:</strong> {item.NOMEPAC} - Horário: {item.HORARIO}
                  </span>
                </div>
              )}

              {item.OBSERVACAO && (
                <div className="mt-1 pt-1 border-top text-secondary small d-flex align-items-start gap-1">
                  <FaStickyNote size={11} className="mt-1 text-muted flex-shrink-0" />
                  <div>
                    <strong>Obs:</strong> {item.OBSERVACAO}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default function AgendaHistoryModal({
  show,
  onHide,
  agendaHistoryEvents = [], // Histórico por CODAGENDA (Aba 1)
  pacienteHistoryEvents = [], // Histórico por CODPAC (Aba 2)
  loading = false,
  error = '',
  pacienteNome = '',
  codAgenda = null,
}) {
  const [activeTab, setActiveTab] = useState('agenda');

  // Garante que ao abrir um novo registro o modal volte para a primeira aba
  useEffect(() => {
    if (show) {
      setActiveTab('agenda');
    }
  }, [show, codAgenda]);

  // Agrupa o histórico global por CODAGENDA (Aba 2)
  const groupedPacienteAgendas = useMemo(() => {
    if (!Array.isArray(pacienteHistoryEvents) || pacienteHistoryEvents.length === 0) return [];

    const map = new Map();
    pacienteHistoryEvents.forEach((item, idx) => {
      const key = item.CODAGENDA ? String(item.CODAGENDA) : `sem-codigo-${idx}`;

      if (!map.has(key)) {
        map.set(key, {
          codAgenda: key,
          dataAgenda: item.DATAAGENDA_FORMATADA,
          procedimento: item.PROCEDIMENTO_DESCRICAO || item.PROCEDIMENTO || 'Consulta/Procedimento',
          convenio: item.CONVENIO,
          items: [],
        });
      }
      map.get(key).items.push(item);
    });

    return Array.from(map.values());
  }, [pacienteHistoryEvents]);

  // Obtém o estado atual da vaga na Aba 1
  const estadoAtualAba1 = agendaHistoryEvents[0]?.ESTADO;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="fs-5 d-flex align-items-center gap-2">
          <FaHistory className="text-primary" />
          Histórico da Agenda
          {pacienteNome && <small className="text-muted fs-6">({pacienteNome})</small>}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-3">
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Carregando históricos...</p>
          </div>
        )}

        {error && !loading && <Alert variant="danger">{error}</Alert>}

        {!loading && !error && (
          <Tabs
            id="agenda-history-tabs"
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k || 'agenda')}
            className="mb-3"
          >
            {/* ABA 1: Histórico do Agendamento Específico (CODAGENDA) */}
            <Tab
              eventKey="agenda"
              title={
                <span>
                  <FaCalendarAlt className="me-1" />
                  Este Agendamento {codAgenda ? `(#${codAgenda})` : ''}
                </span>
              }
            >
              {agendaHistoryEvents.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  Nenhuma alteração registrada para este agendamento especificamente.
                </div>
              ) : (
                <>
                  {estadoAtualAba1 && (
                    <div className="d-flex align-items-center justify-content-between bg-light p-2 rounded mb-3 border">
                      <span className="small text-muted fw-semibold">
                        Estado Atual do Agendamento:
                      </span>
                      {getStatusBadge(estadoAtualAba1)}
                    </div>
                  )}
                  <HistoryTimeline items={agendaHistoryEvents} />
                </>
              )}
            </Tab>

            {/* ABA 2: Histórico Global de todas as Agendas do Paciente (CODPAC) */}
            <Tab
              eventKey="paciente"
              title={
                <span>
                  <FaUser className="me-1" />
                  Todos Agendamentos do Paciente
                </span>
              }
            >
              {groupedPacienteAgendas.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  Nenhum agendamento anterior encontrado para este paciente.
                </div>
              ) : (
                <Accordion defaultActiveKey={groupedPacienteAgendas[0]?.codAgenda} alwaysOpen>
                  {groupedPacienteAgendas.map((group, gIndex) => {
                    const ultimoEstado = group.items[0]?.ESTADO;

                    return (
                      <Accordion.Item
                        key={group.codAgenda || `group-${gIndex}`}
                        eventKey={group.codAgenda}
                        className="mb-3 border rounded shadow-sm overflow-hidden"
                      >
                        <Accordion.Header>
                          <div className="d-flex justify-content-between align-items-center w-100 pe-3 flex-wrap gap-2">
                            <div className="d-flex align-items-center gap-2">
                              <FaCalendarAlt className="text-primary" />
                              <span className="fw-bold">
                                *{group.items[0].DATAAGENDA_FORMATADA || group.dataAgenda} - (#
                                {group.codAgenda})
                              </span>
                              <span
                                className="text-muted ms-2 text-truncate"
                                style={{ maxWidth: 200 }}
                                title={group.procedimento}
                              >
                                — {group.procedimento}
                              </span>
                              {group.convenio && (
                                <Badge bg="light" text="dark" className="border ms-1">
                                  {group.convenio}
                                </Badge>
                              )}
                            </div>
                            <div>Último estado: {getStatusBadge(ultimoEstado)}</div>
                          </div>
                        </Accordion.Header>

                        <Accordion.Body className="bg-white pt-3 pb-2">
                          <HistoryTimeline items={group.items} />
                        </Accordion.Body>
                      </Accordion.Item>
                    );
                  })}
                </Accordion>
              )}
            </Tab>
          </Tabs>
        )}
      </Modal.Body>

      <Modal.Footer className="bg-light">
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
